import React, { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PostAddIcon from "@mui/icons-material/PostAdd";
import i18next from "i18next";
import { BRAND, useLightStyles, DOSSIER_STATUS } from "../lightTheme";
import { Context } from "states/Store";
import { getDoctypeTranslatedName } from "services/common/DocumentUtils";
import { getAccountDossiersSearch } from "services/edge/DossierService";
import { getUrlWithAccountId } from "config/Routes";

const useStyles = makeStyles()(() => ({
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    fontSize: "0.8rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    marginBottom: "2rem",
    width: "fit-content",
    "&:hover": { color: BRAND.primary },
    transition: "color 0.15s",
  },

  // ── Recap card ──
  recapCard: {
    borderRadius: "12px",
    overflow: "hidden",
    border: `1px solid ${BRAND.border}`,
    marginBottom: "1.5rem",
  },

  // Header vert foncé
  recapHeader: {
    backgroundColor: BRAND.background,
    borderBottom: `1px solid ${BRAND.border}`,
    padding: "1.25rem 1.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  recapHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  recapHeaderLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  recapHeaderTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },
  recapHeaderIconWrap: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: `${BRAND.primary}10`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // Body blanc
  recapBody: {
    backgroundColor: BRAND.white,
    padding: "1.5rem 1.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  recapSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  recapSectionLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  recapSectionRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  recapSectionValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },
  recapSectionMeta: {
    fontSize: "0.78rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  recapDivider: {
    height: "1px",
    backgroundColor: BRAND.border,
  },
  onboardedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    backgroundColor: BRAND.tertiary,
    color: BRAND.primary,
    borderRadius: "20px",
    padding: "2px 8px",
    fontSize: "0.7rem",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
  },

  // Attributes list
  attrGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.5rem 1rem",
    marginTop: "0.25rem",
    "@media (max-width: 480px)": { gridTemplateColumns: "1fr" },
  },
  attrRow: {
    display: "flex",
    flexDirection: "column",
    gap: "0.15rem",
  },
  attrKey: {
    fontSize: "0.68rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  attrVal: {
    fontSize: "0.85rem",
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
  },

  // Documents list
  docsList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    marginTop: "0.25rem",
  },
  docItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.82rem",
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },

  // CTA
  launchBtn: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    justifyContent: "center",
    width: "100%",
    padding: "0.9rem 2rem",
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    border: "none",
    borderRadius: "12px",
    fontFamily: "Inter, sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.15s",
    boxShadow: "0 4px 16px rgba(0,58,34,0.22)",
    "&:hover": { opacity: 0.9, transform: "translateY(-1px)" },
    "&:active": { transform: "translateY(0)" },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed", transform: "none" },
  },
}));

function AttrList({ attrs, label, icon, classes: s, cx, light }) {
  const filled = (attrs ?? []).filter((a) => a.values?.length > 0);
  if (filled.length === 0) return null;
  return (
    <>
      <Box className={s.recapDivider} />
      <Box className={s.recapSection}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          {icon}
          <Typography className={s.recapSectionLabel}>{label}</Typography>
        </Box>
        <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {filled.map((a) => {
            const vals = Array.isArray(a.values) ? a.values.join(", ") : a.values;
            return (
              <Box key={a.label ?? a.id} className={light.attrTag}>
                <span className={light.attrTagLabel}>{a.label ?? a.id}</span>
                <span className={light.attrTagValue}>{vals}</span>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
}

export default function StepConfirm({ thirdparty, dossierTypes = [], selectedContext, onBack, onLaunch, launching, stepper }) {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const [duplicateWarnings, setDuplicateWarnings] = useState({}); // { [code]: dossier | false | null }
  const [archivedWarnings, setArchivedWarnings] = useState({});

  // Back-compat: also accept single dossierType prop
  const dossierType = dossierTypes[0] ?? null;

  useEffect(() => {
    if (!state.account?.id || !thirdparty?.name || dossierTypes.length === 0) return;
    const sort = [{ field: "creationDate", sort: "desc" }];
    dossierTypes.forEach((dt) => {
      const searchParams = { dossiers: [{ name: thirdparty.name, dossierType: dt.code }] };
      getAccountDossiersSearch(state.account.id, true, null, { page: 0, rowPerPage: 1 }, searchParams, sort, dispatch)
        .then((res) => {
          const existing = res?.content ?? [];
          setDuplicateWarnings((prev) => ({ ...prev, [dt.code]: existing.length > 0 ? existing[0] : false }));
        })
        .catch(() => setDuplicateWarnings((prev) => ({ ...prev, [dt.code]: false })));
      getAccountDossiersSearch(state.account.id, false, null, { page: 0, rowPerPage: 1 }, searchParams, sort, dispatch)
        .then((res) => {
          const existing = res?.content ?? [];
          setArchivedWarnings((prev) => ({ ...prev, [dt.code]: existing.length > 0 ? existing[0] : false }));
        })
        .catch(() => setArchivedWarnings((prev) => ({ ...prev, [dt.code]: false })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id, thirdparty?.name, dossierTypes.map((d) => d.code).join(",")]);

  return (
    <Box className={light.pageRoot}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner} style={{ maxWidth: "1100px" }}>
          <Box className={s.backBtn} onClick={onBack} style={{ color: "rgba(255,255,255,0.7)", marginBottom: "0.75rem" }}>
            <ArrowBackIcon style={{ fontSize: "16px" }} />
            {dossierType?.pendingDossierAttributes?.length > 0 ? i18next.t("light.launch.backToAttrs") : i18next.t("light.launch.backToEvalChoice")}
          </Box>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.launch.recap")}</Typography>
          <Typography className={light.pageSubtitleWhite}>
            {i18next.t("light.launch.recapSubtitle")}
          </Typography>
        </Box>
      </Box>
      <Box className={light.pageInner} style={{ maxWidth: "1100px", marginTop: "-0.75rem" }}>
        <Box className={light.card} style={{ overflow: "hidden", padding: 0 }}>
          {stepper}
          <Box style={{ padding: "1.25rem" }}>

        {/* Duplicate warnings */}
        {dossierTypes.map((dt) => {
          const dup = duplicateWarnings[dt.code];
          const arch = archivedWarnings[dt.code];
          if (dup && dup !== false) {
            return (
              <Box key={`dup-${dt.code}`} style={{
                display: "flex", alignItems: "flex-start", gap: "0.75rem",
                backgroundColor: "#FFF7ED", border: "1.5px solid #FED7AA",
                borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1.25rem",
              }}>
                <WarningAmberIcon style={{ fontSize: "18px", color: "#B45309", flexShrink: 0, marginTop: "1px" }} />
                <Box>
                  <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: "#92400E", fontFamily: "Inter", marginBottom: "0.2rem" }}>
                    {i18next.t("light.launch.dupWarningTitle")}
                  </Typography>
                  <Typography style={{ fontSize: "0.78rem", color: "#B45309", fontFamily: "Inter", lineHeight: 1.5 }}>
                    {i18next.t("light.launch.dupWarningMessage", { type: dt.libelle, name: thirdparty?.name, status: (DOSSIER_STATUS[dup.statusCode]?.label) ?? dup.statusCode ?? "—" })}
                  </Typography>
                  <a
                    href={getUrlWithAccountId(state.account.id, `light/dossiers/${dup.dossierId}`)}
                    style={{ fontSize: "0.78rem", color: "#92400E", fontFamily: "Inter", fontWeight: 700, marginTop: "0.35rem", display: "inline-block" }}
                  >
                    {i18next.t("light.launch.viewEval")} →
                  </a>
                </Box>
              </Box>
            );
          }
          if (arch && arch !== false && !dup) {
            return (
              <Box key={`arch-${dt.code}`} style={{
                display: "flex", alignItems: "flex-start", gap: "0.75rem",
                backgroundColor: "#FFF7ED", border: "1.5px solid #FED7AA",
                borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1.25rem",
              }}>
                <WarningAmberIcon style={{ fontSize: "18px", color: "#B45309", flexShrink: 0, marginTop: "1px" }} />
                <Box>
                  <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: "#92400E", fontFamily: "Inter", marginBottom: "0.2rem" }}>
                    {i18next.t("light.launch.archivedWarningTitle")}
                  </Typography>
                  <Typography style={{ fontSize: "0.78rem", color: "#B45309", fontFamily: "Inter", lineHeight: 1.5 }}>
                    {i18next.t("light.launch.archivedWarningMessage", { type: dt.libelle, name: thirdparty?.name })}
                  </Typography>
                  <a
                    href={getUrlWithAccountId(state.account.id, `light/dossiers/${arch.dossierId}`)}
                    style={{ fontSize: "0.78rem", color: "#92400E", fontFamily: "Inter", fontWeight: 700, marginTop: "0.35rem", display: "inline-block" }}
                  >
                    {i18next.t("light.launch.viewArchivedEval")} →
                  </a>
                </Box>
              </Box>
            );
          }
          return null;
        })}

        {/* Recap card */}
        <Box className={s.recapCard}>

          {/* Header — évaluation(s) */}
          <Box className={s.recapHeader}>
            <Box className={s.recapHeaderIconWrap}>
              <FolderOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
            </Box>
            <Box className={s.recapHeaderText}>
              <Typography className={s.recapHeaderLabel}>
                {dossierTypes.length > 1
                  ? `${dossierTypes.length} ${i18next.t("light.launch.evalsToLaunch")}`
                  : i18next.t("light.launch.evalToLaunch")
                }
              </Typography>
              <Typography className={s.recapHeaderTitle}>{dossierTypes.map((dt) => dt.libelle).join(", ")}</Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box className={s.recapBody}>

            {/* Fournisseur */}
            <Box className={s.recapSection}>
              <Typography className={s.recapSectionLabel}>{i18next.t("light.common.supplier")}</Typography>
              <Box className={s.recapSectionRow}>
                <Box className={cx(light.iconWrap, light.iconWrapSecondary)} style={{ width: 36, height: 36 }}>
                  <BusinessOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
                </Box>
                <Box>
                  <Typography className={s.recapSectionValue}>{thirdparty?.name}</Typography>
                  {(thirdparty?.identifier || thirdparty?.city) && (
                    <Typography className={s.recapSectionMeta}>
                      {[thirdparty.identifier, thirdparty.postCode, thirdparty.city].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Box>
                {thirdparty?.onboarded && (
                  <Box className={s.onboardedBadge} style={{ marginLeft: "auto" }}>
                    <CheckCircleOutlineIcon style={{ fontSize: "11px" }} />
                    {i18next.t("light.launch.inPortfolio")}
                  </Box>
                )}
              </Box>
            </Box>

            <AttrList
              attrs={thirdparty?.pendingAttributes}
              label={i18next.t("light.launch.tpAttrs")}
              icon={<BusinessOutlinedIcon style={{ fontSize: "13px", color: BRAND.textMuted }} />}
              classes={s} cx={cx} light={light}
            />

            <AttrList
              attrs={dossierType?.pendingDossierAttributes}
              label={i18next.t("light.launch.evalAttrs")}
              icon={<FolderOutlinedIcon style={{ fontSize: "13px", color: BRAND.textMuted }} />}
              classes={s} cx={cx} light={light}
            />

            {/* Context thirdparty attrs */}
            {selectedContext && (
              <Box className={s.recapSection}>
                <Typography className={s.recapSectionLabel}>{selectedContext.modelName || i18next.t("light.contexts.title")}</Typography>
                <Box className={s.recapSectionRow}>
                  <Box className={cx(light.iconWrap, light.iconWrapSecondary)} style={{ width: 36, height: 36 }}>
                    <AccountTreeOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
                  </Box>
                  <Box>
                    <Typography className={s.recapSectionValue}>{selectedContext.reference}</Typography>
                    {selectedContext.isNew && (
                      <Typography className={s.recapSectionMeta}>
                        {i18next.t("light.launch.newContext")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            <Box className={s.recapDivider} />

            {/* Documents requis */}
            {dossierTypes.some((dt) => dt.requirements?.length > 0 || dt.extraDocs?.length > 0) && (
              dossierTypes.filter((dt) => dt.requirements?.length > 0 || dt.extraDocs?.length > 0).map((dt) => (
                <Box key={dt.code} className={s.recapSection}>
                  <Typography className={s.recapSectionLabel}>
                    {dossierTypes.length > 1 ? `${dt.libelle} — ` : ""}{i18next.t("light.launch.docsRequired", { count: (dt.requirements?.length ?? 0) + (dt.extraDocs?.length ?? 0) })}
                  </Typography>
                  <Box className={s.docsList}>
                    {(dt.requirements ?? []).map((req, i) => (
                      <Box key={req.code ?? i} className={s.docItem}>
                        <ArticleOutlinedIcon style={{ fontSize: "15px", color: BRAND.secondary, flexShrink: 0 }} />
                        {getDoctypeTranslatedName(state.doctypeTraductions, req.code, state.lang) || req.descriptionFr || req.name || req.code}
                      </Box>
                    ))}
                  </Box>
                  {dt.extraDocs?.length > 0 && (
                    <>
                      <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.5rem" }}>
                        <PostAddIcon style={{ fontSize: "14px", color: BRAND.primary }} />
                        <Typography style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: BRAND.primary, fontFamily: "Inter, sans-serif" }}>
                          {i18next.t("light.launch.extraDocsAdded", { count: dt.extraDocs.length })}
                        </Typography>
                      </Box>
                      <Box className={s.docsList}>
                        {dt.extraDocs.map((code) => (
                          <Box key={code} className={s.docItem}>
                            <PostAddIcon style={{ fontSize: "15px", color: BRAND.primary, flexShrink: 0 }} />
                            {getDoctypeTranslatedName(state.doctypeTraductions, code, state.lang) || code}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Launch CTA */}
        <button
          className={s.launchBtn}
          onClick={onLaunch}
          disabled={!!launching}
        >
          <RocketLaunchOutlinedIcon style={{ fontSize: "20px" }} />
          {i18next.t("light.launch.launchBtn")}
        </button>

          </Box>
        </Box>
      </Box>
    </Box>
  );
}
